import * as styles from "./DatePickerHeader.css";

import { clsx } from "clsx";
import type { JSX } from "react";
import { useMemo } from "react";

import { Icon } from "@frontend/components/atoms/Icon";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@frontend/components/atoms/Select";
import { useLingui } from "@lingui/react/macro";

import { useDatePickerContext } from "./DatePicker.context";
import type { DatePickerHeaderProps } from "./DatePicker.types";
import { addMonths, today } from "./dateMath";

export function DatePickerHeader({
  className,
  monthOffset = 0,
  hidePrevious = false,
  hideNext = false,
}: DatePickerHeaderProps): JSX.Element {
  const { t } = useLingui();

  const { viewYear, viewMonth, locale, min, max, goToMonth, goToView } =
    useDatePickerContext();

  // The month this header names. With two calendars the second one is a month
  // ahead of the view, so picking there shifts the view back by its offset.
  const { year: headerYear, month: headerMonth } = addMonths(
    viewYear,
    viewMonth,
    monthOffset,
  );

  const goToHeaderView = (year: number, month: number) => {
    const shifted = addMonths(year, month, -monthOffset);

    goToView(shifted.year, shifted.month);
  };

  const monthOptions = useMemo(
    () =>
      Array.from({ length: 12 }, (_, month) => ({
        value: String(month),
        label: new Intl.DateTimeFormat(locale, { month: "long" }).format(
          new Date(2023, month, 1),
        ),
      })),
    [locale],
  );

  const yearOptions = useMemo(() => {
    const minYear = min?.year ?? today().year - 100;
    const maxYear = max?.year ?? today().year + 10;

    return Array.from({ length: maxYear - minYear + 1 }, (_, index) => {
      const year = minYear + index;

      return { value: String(year), label: String(year) };
    });
  }, [min, max]);

  return (
    <div
      data-slot="date-picker-header"
      className={clsx(styles.header, className)}
    >
      {hidePrevious ? (
        <span className={styles.navSpacer} />
      ) : (
        <button
          type="button"
          className={styles.navButton}
          onClick={() => goToMonth(-1)}
          aria-label={t`Previous month`}
        >
          <Icon name="ChevronLeft" size="sm" />
        </button>
      )}

      <div className={styles.selects}>
        <Select
          size="sm"
          items={monthOptions}
          value={String(headerMonth)}
          onValueChange={(value) => {
            if (value) {
              goToHeaderView(headerYear, Number(value));
            }
          }}
        >
          <SelectTrigger className={styles.monthSelectTrigger}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {monthOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          size="sm"
          items={yearOptions}
          value={String(headerYear)}
          onValueChange={(value) => {
            if (value) {
              goToHeaderView(Number(value), headerMonth);
            }
          }}
        >
          <SelectTrigger className={styles.yearSelectTrigger}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {yearOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {hideNext ? (
        <span className={styles.navSpacer} />
      ) : (
        <button
          type="button"
          className={styles.navButton}
          onClick={() => goToMonth(1)}
          aria-label={t`Next month`}
        >
          <Icon name="ChevronRight" size="sm" />
        </button>
      )}
    </div>
  );
}
